import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { PolicyContextProvider, setToken, useError, useUser, useLoader, useAnalytics, parseToken, } from "@piiqtechnologies/ui-lib";
import { useLocation, useNavigate } from "react-router-dom";
import { PolicyGroups } from "@constants/Policies";
import { isBetaFeatureEnabled } from "@utils";
import { generateAllowedPolicies, getUserContext, isAdminUser, getRedirectPathAfterLogin, storeUserContext, } from "./utils";
import * as Sentry from "@sentry/react";
export const AuthContext = createContext(null);
const AuthContextProvider = ({ children }) => {
    var _a, _b, _c, _d;
    const [userContext, setUserContext] = useState(getUserContext());
    const [getUser, user, getUserInProgress, getUserError] = useUser();
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [denyPolicies, setDenyPolicies] = useState([]);
    const analytics = useAnalytics();
    const navigate = useNavigate();
    const { state: locationState } = useLocation();
    const { setError } = useError();
    const { setLoader } = useLoader();
    useEffect(() => {
        setLoader(getUserInProgress, "getUserInProgress");
        return () => setLoader(false, "getUserInProgress");
    }, [getUserInProgress]);
    useEffect(() => {
        if (getUserError) {
            setError(getUserError === null || getUserError === void 0 ? void 0 : getUserError.message);
        }
    }, [getUserError]);
    const isAdmin = useMemo(() => isAdminUser(userContext), [userContext]);
    const redirectPathAfterLogin = useMemo(() => getRedirectPathAfterLogin(user), [user]);
    const onLogin = (loggedInUser, sso) => {
        setShouldNavigate(true);
        const { token } = loggedInUser;
        const { proxyUser } = parseToken(token);
        getUser({ userId: "__current" });
        setToken(token);
        setUserContext((_userContext) => (Object.assign(Object.assign({}, _userContext), { sso, proxyUser })));
    };
    useEffect(() => {
        var _a, _b, _c, _d;
        if ((_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a._id) {
            analytics.identify(userContext.user._id, {
                name: userContext.user.name,
                role: (_b = userContext.user.role) === null || _b === void 0 ? void 0 : _b.name,
                organization: (_c = userContext.user.organization) === null || _c === void 0 ? void 0 : _c.name,
                organizationId: (_d = userContext.user.organization) === null || _d === void 0 ? void 0 : _d._id,
            });
            Sentry.setUser({
                id: userContext.user._id,
            });
        }
    }, [userContext]);
    useEffect(() => {
        if (!user)
            return;
        setUserContext((_userContext) => {
            storeUserContext(Object.assign(Object.assign({}, _userContext), { user }));
            return Object.assign(Object.assign({}, _userContext), { user });
        });
        let redirectPath = redirectPathAfterLogin;
        if (locationState) {
            const { redirectTo } = locationState;
            redirectPath = `${redirectTo.pathname}${redirectTo.search}`;
        }
        shouldNavigate && navigate(redirectPath, { replace: true });
    }, [user]);
    const onLogout = () => {
        setToken(null);
        sessionStorage.clear();
        setUserContext(null);
        Sentry.configureScope((scope) => {
            scope.setUser(null);
        });
    };
    const reloadCurrentUser = () => {
        setShouldNavigate(false);
        getUser({ userId: "__current" });
    };
    const syncUserContext = (partialUser) => {
        setUserContext((_userContext) => {
            const newUserContext = Object.assign(Object.assign({}, _userContext), { user: Object.assign(Object.assign({}, _userContext.user), partialUser) });
            storeUserContext(newUserContext);
            return newUserContext;
        });
    };
    const updateDenyPolicies = (policy) => {
        setDenyPolicies([policy, ...denyPolicies]);
    };
    const userFeatures = useMemo(() => { var _a; return ((_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a.features) || []; }, [userContext]);
    const featureGatedPolicies = useMemo(() => {
        // check if user has access to feature if its beta feature
        return PolicyGroups.FEATURE_GATING.filter((policy) => {
            var _a, _b, _c;
            if ((userContext === null || userContext === void 0 ? void 0 : userContext.user) && isBetaFeatureEnabled(policy)) {
                return (_b = (_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a.allow) === null || _b === void 0 ? void 0 : _b.includes(`peek:users:${(_c = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _c === void 0 ? void 0 : _c._id}::${policy}`);
            }
            return true;
        });
    }, [userContext === null || userContext === void 0 ? void 0 : userContext.user]);
    const allowedPolicies = useMemo(() => {
        var _a, _b, _c;
        return generateAllowedPolicies(((_b = (_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a.role) === null || _b === void 0 ? void 0 : _b.alias) || "", userFeatures, featureGatedPolicies, ((_c = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _c === void 0 ? void 0 : _c.deny) || []);
    }, [(_b = (_a = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _a === void 0 ? void 0 : _a.role) === null || _b === void 0 ? void 0 : _b.alias, userFeatures, featureGatedPolicies, (_c = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _c === void 0 ? void 0 : _c.deny]);
    return (React.createElement(AuthContext.Provider, { value: {
            user: userContext === null || userContext === void 0 ? void 0 : userContext.user,
            proxyUser: userContext === null || userContext === void 0 ? void 0 : userContext.proxyUser,
            sso: userContext === null || userContext === void 0 ? void 0 : userContext.sso,
            onLogin,
            isAdmin,
            onLogout,
            syncUserContext,
            reloadCurrentUser,
            updateDenyPolicies,
        } },
        React.createElement(PolicyContextProvider, { allow: allowedPolicies, deny: [...denyPolicies, ...(((_d = userContext === null || userContext === void 0 ? void 0 : userContext.user) === null || _d === void 0 ? void 0 : _d.deny) || [])] }, children)));
};
export function useAuthContext() {
    return useContext(AuthContext);
}
export default AuthContextProvider;
